import React, { useState } from "react";

import { FileDownload } from "@mui/icons-material";
import {
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Tabs,
  Tab,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  IconButton,
  Tooltip,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { useSelector } from "react-redux";

import { apiClient } from "../api/client";
import { DailyAttendanceChart, HolidayClubAttendanceChart, OutsideSchoolHoursCareChart } from "../components/charts";
import { MainLayout } from "../components/layout/MainLayout";
import { CACard } from "../components/ui";
import { useDailyAttendance, useHolidayClubAttendanceByDate, useTermOptions, useDownloadCsv } from "../hooks";
import { RootState } from "../store";

dayjs.extend(isBetween);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`attendance-tabpanel-${index}`}
      aria-labelledby={`attendance-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

export const AttendancePage: React.FC = () => {
  const selectedService = useSelector((state: RootState) => state.service.selectedService);
  const [activeTab, setActiveTab] = useState(0);

  // Selection mode: 'dateRange' or 'termYear'
  const [selectionMode, setSelectionMode] = useState<"dateRange" | "termYear">("dateRange");

  // Date range state (default to last 30 days)
  const [startDate, setStartDate] = useState(dayjs().subtract(30, "days"));
  const [endDate, setEndDate] = useState(dayjs());

  // Term selection state
  const [selectedTermOption, setSelectedTermOption] = useState<string>("");

  // Applied filters (what's actually used for API calls)
  const [appliedStartDate, setAppliedStartDate] = useState(dayjs().subtract(30, "days"));
  const [appliedEndDate, setAppliedEndDate] = useState(dayjs());
  const [appliedMode, setAppliedMode] = useState<"dateRange" | "termYear">("dateRange");

  // Get term options from previous year to current year
  const currentYear = new Date().getFullYear();
  const { termOptions } = useTermOptions(selectedService?.serviceId || null, currentYear - 1);

  // Download CSV hook
  const { isDownloading, downloadCsv } = useDownloadCsv(
    (payload) => {
      const data = payload as { serviceId: number; start: dayjs.Dayjs; end: dayjs.Dayjs };
      return apiClient.attendance.downloadCsv(data.serviceId, data.start, data.end);
    }
  );

  // Find the selected term details
  const selectedTerm = termOptions.find((option) => option.value === selectedTermOption);

  // Auto-select the current term if none is selected and options are available
  React.useEffect(() => {
    if (!selectedTermOption && termOptions.length > 0) {
      // Find the current term based on today's date
      const today = dayjs();
      const currentTerm = termOptions.find((option) => today.isBetween(option.startDate, option.endDate, "day", "[]"));
      if (currentTerm) {
        setSelectedTermOption(currentTerm.value);
      } else {
        // Default to the first available term
        setSelectedTermOption(termOptions[0].value);
      }
    }
  }, [termOptions, selectedTermOption]);

  // Get current active date range for API calls
  const getCurrentDateRange = () => {
    if (appliedMode === "dateRange") {
      return { start: appliedStartDate, end: appliedEndDate };
    } else if (selectedTerm) {
      return { start: selectedTerm.startDate, end: selectedTerm.endDate };
    } else {
      // Fallback to current date range if no term selected
      return { start: appliedStartDate, end: appliedEndDate };
    }
  };

  const currentDateRange = getCurrentDateRange();

  // All hooks now use the applied date range parameters
  const {
    data: dailyAttendanceData,
    loading: dailyAttendanceLoading,
    error: dailyAttendanceError,
  } = useDailyAttendance(selectedService?.serviceId || null, currentDateRange.start, currentDateRange.end);

  const {
    data: holidayClubData,
    loading: holidayClubLoading,
    error: holidayClubError,
  } = useHolidayClubAttendanceByDate(selectedService?.serviceId || null, currentDateRange.start, currentDateRange.end);

  const {
    data: outsideSchoolData,
    loading: outsideSchoolLoading,
    error: outsideSchoolError,
  } = useDailyAttendance(selectedService?.serviceId || null, currentDateRange.start, currentDateRange.end);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSelectionModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectionMode(event.target.value as "dateRange" | "termYear");
  };

  const handleTermChange = (event: SelectChangeEvent<string>) => {
    setSelectedTermOption(event.target.value as string);
  };

  const handleGoClick = () => {
    // Apply current selections
    setAppliedMode(selectionMode);
    if (selectionMode === "dateRange") {
      setAppliedStartDate(startDate);
      setAppliedEndDate(endDate);
    }
    // For term selection, the getCurrentDateRange function will handle it
  };

  const handleDownloadCsv = async () => {
    if (!selectedService?.serviceId || isDownloading) {
      return;
    }

    try {
      const { start, end } = getCurrentDateRange();
      await downloadCsv({
        data: { serviceId: selectedService.serviceId, start, end },
        fileName: `Attendance from ${start.format("YYYY-MM-DD")} to ${end.format("YYYY-MM-DD")}.csv`,
      });
    } catch (error) {
      console.error("Failed to download CSV:", error);
      // You might want to show an error message to the user here
    }
  };

  return (
    <MainLayout>
      <CACard>
        {selectedService && (
          <>
            {/* Date Range / Term Selection Controls */}
            <Box sx={{ p: 3, backgroundColor: "#f5f5f5", borderRadius: 1, mb: 3 }}>
              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <FormLabel component="legend">Selection Mode</FormLabel>
                <RadioGroup row value={selectionMode} onChange={handleSelectionModeChange} aria-label="selection mode">
                  <FormControlLabel value="dateRange" control={<Radio />} label="Date Range" />
                  <FormControlLabel value="termYear" control={<Radio />} label="Term / Year" />
                </RadioGroup>
              </FormControl>

              <Box sx={{ display: "flex", gap: 2, alignItems: "end", flexWrap: "wrap" }}>
                {selectionMode === "dateRange" ? (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={(newValue) => setStartDate(newValue || dayjs())}
                      format="DD/MM/YYYY"
                      sx={{ minWidth: 150 }}
                    />
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={(newValue) => setEndDate(newValue || dayjs())}
                      format="DD/MM/YYYY"
                      sx={{ minWidth: 150 }}
                    />
                  </LocalizationProvider>
                ) : (
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Year - Term</InputLabel>
                    <Select value={selectedTermOption} label="Year - Term" onChange={handleTermChange}>
                      {termOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <Button variant="contained" onClick={handleGoClick} sx={{ ml: 2 }}>
                  Go
                </Button>
                <Tooltip title="Download CSV">
                  <IconButton onClick={handleDownloadCsv} color="primary" disabled={isDownloading} sx={{ ml: 1 }}>
                    {isDownloading ? <CircularProgress size={24} /> : <FileDownload />}
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Current Range: {currentDateRange.start.format("MMM DD, YYYY")} -{" "}
                  {currentDateRange.end.format("MMM DD, YYYY")}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
              <Tabs value={activeTab} onChange={handleTabChange} aria-label="attendance tabs">
                <Tab label="Daily Attendance" />
                <Tab label="Rocketeers" />
                <Tab label="Your OSHC" />
              </Tabs>
            </Box>

            <TabPanel value={activeTab} index={0}>
              <Grid container spacing={3}>
                <Grid size={12}>
                  {dailyAttendanceLoading ? (
                    <CircularProgress />
                  ) : dailyAttendanceError ? (
                    <Alert severity="error">{dailyAttendanceError}</Alert>
                  ) : (
                    <DailyAttendanceChart data={dailyAttendanceData} title="Daily Average Attendance" height={400} />
                  )}
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <Grid container spacing={3}>
                <Grid size={12}>
                  {holidayClubLoading ? (
                    <CircularProgress />
                  ) : holidayClubError ? (
                    <Alert severity="error">{holidayClubError}</Alert>
                  ) : (
                    <HolidayClubAttendanceChart
                      data={holidayClubData}
                      title="Rocketeers Attendance"
                      height={400}
                      chartType="bar"
                    />
                  )}
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
              <Grid container spacing={3}>
                <Grid size={12}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Weekly attendance averages grouped by week beginning for the selected date range:{" "}
                    {currentDateRange.start.format("MMM DD, YYYY")} - {currentDateRange.end.format("MMM DD, YYYY")}
                  </Typography>
                  {outsideSchoolLoading ? (
                    <CircularProgress />
                  ) : outsideSchoolError ? (
                    <Alert severity="error">{outsideSchoolError}</Alert>
                  ) : (
                    <OutsideSchoolHoursCareChart
                      data={outsideSchoolData}
                      isLoading={outsideSchoolLoading}
                      title="Your OSHC - Daily Patterns"
                      height={400}
                      startDate={currentDateRange.start}
                      endDate={currentDateRange.end}
                    />
                  )}
                </Grid>
              </Grid>
            </TabPanel>
          </>
        )}
        {!selectedService && (
          <Typography variant="body1" color="text.secondary">
            Please select a service to view attendance reports.
          </Typography>
        )}
      </CACard>
    </MainLayout>
  );
};
